import type { UpdateProfileBody } from "@nlc/shared-types";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  canDeleteOrArchiveUser,
  isCustomerRole,
  isTeamRole,
  updateProfileBodyZod,
  USER_ROLES,
  UserRoleEnum,
} from "@nlc/shared-types";
import { useParams, useRouter } from "@tanstack/react-router";
import { ArchiveIcon, LockIcon, TagIcon, Trash2Icon } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import { Avatar } from "@/components/avatar";
import { Badge } from "@/components/badge";
import { Button } from "@/components/button";
import {
  ComboBox,
  ComboBoxButton,
  ComboBoxGroup,
  ComboBoxInput,
  ComboBoxListBox,
  ComboBoxListItem,
  ComboBoxPopover,
} from "@/components/combobox";
import { Input, Label, TextField } from "@/components/field";
import { Loader } from "@/components/Loader";
import {
  Select,
  SelectButton,
  SelectListBox,
  SelectListItem,
  SelectPopover,
} from "@/components/select";
import { Tab, TabList, TabPanel, Tabs } from "@/components/tabs";
import { useRoleAccess } from "@/features/auth/permissions/useRoleAccess";
import { ProfileCollaboratorFields } from "@/features/profile/components/ProfileCollaboratorFields";
import { ProfileOwnerAdminFields } from "@/features/profile/components/ProfileOwnerAdminFields";
import { ProfileTeamFields } from "@/features/profile/components/ProfileTeamFields";
import { PerformanceTab } from "@/features/profile/components/tabs/PerformanceTab";
import { TagListField } from "@/features/profile/components/TagListField";
import { TeamRequestsTab } from "@/features/requests/components/tabs/teamlead";
import { FeaturePageLayout } from "@/features/shared";
import { useUser } from "@/hooks/useUser";

import { useGetProjectsByOwnerOfUser } from "../hooks/useGetProjectsByOwnerOfUser";
import { useResolveTimezones } from "../hooks/useResolveTimezones";
import { useResolveUpdateRoles } from "../hooks/useResolveUpdateRoles";
import { AvatarCropModal } from "../shared/components/AvatarCropModal";
import { useAvatarUpload } from "../shared/hooks/useAvatarUpload";
import ArchiveUser from "./components/ArchiveUser";
import { AssignedToMeTab } from "./components/AssignedToMeTab";
import DeleteUser from "./components/DeleteUser";
import { ImpersonateUser } from "./components/ImpersonateUser";
import UnarchiveUser from "./components/UnarchiveUser";
import UpdateUserPassword from "./components/UpdateUserPassword";
import { useAssignBrandsToUser } from "./hooks/useAssignBrandsToUser";
import { useAssignProjectsToUser } from "./hooks/useAssignProjectsToUser";
import { useAssignRequestsToUser } from "./hooks/useAssignRequestsToUser";
import { useGetUser } from "./hooks/useGetUser";
import { useUpdateUserEmail } from "./hooks/useUpdateUserEmail";
import { useUpdateUserProfile } from "./hooks/useUpdateUserProfile";
import { useUsersTabs } from "./hooks/useUsersTabs";

type OpenModal = "archive" | "delete" | "password" | "unarchive" | null;

export default function UserPage() {
  const [openModal, setOpenModal] = useState<OpenModal>(null);
  const router = useRouter();

  const { userId } = useParams({
    from: "/_auth/_app-shell/users/$userId/edit",
  });
  const { data: userData, isLoading } = useGetUser(userId);
  const { data: loggedInUserData } = useUser();
  const user = userData?.user;
  const loggedInUser = loggedInUserData?.user;

  const { hasRole } = useRoleAccess();

  const isAdminUserViewingOwner =
    hasRole([USER_ROLES.ADMIN]) && user?.roleId === UserRoleEnum.Owner;
  const isUserCollaborator = hasRole([USER_ROLES.COLLABORATOR]);
  const isUserDesigner = hasRole([USER_ROLES.DESIGNER]);

  useEffect(() => {
    if (isAdminUserViewingOwner || isUserCollaborator || isUserDesigner) {
      router.history.go(-1);
    }
  }, [isAdminUserViewingOwner, isUserCollaborator, isUserDesigner, router]);

  const { data: timezones } = useResolveTimezones();
  const { data: allAvailableRoles } = useResolveUpdateRoles(userId);
  const { data: ownerProjects } = useGetProjectsByOwnerOfUser(userId);

  const { mutate: updateProfile } = useUpdateUserProfile(userId);
  const { mutate: updateEmail } = useUpdateUserEmail(userId);
  const { mutate: updateBrands } = useAssignBrandsToUser(userId);
  const { mutate: updateProjects } = useAssignProjectsToUser(userId);
  const { mutate: updateRequests } = useAssignRequestsToUser(userId);

  const {
    cropImageSrc,
    fileInputRef,
    handleChangePhoto,
    handleCropApply,
    handleCropClose,
    handleFileSelect,
    handleRemove,
    isUploading: isAvatarUploading,
  } = useAvatarUpload(userId);

  const viewedUserProjectIds = useMemo(
    () => userData?.projects?.map((p) => p.id) ?? [],
    [userData],
  );

  const userProjectItems = useMemo(
    () => userData?.projects.map((p) => ({ id: p.id, name: p.name })) ?? [],
    [userData],
  );

  const { control, handleSubmit: hookFormSubmit } = useForm<UpdateProfileBody>({
    resolver: zodResolver(updateProfileBodyZod),
    values: {
      city: user?.city || "",
      country: user?.country || "",
      customerSuccessId: user?.customerSuccessId || undefined,
      designationId: user?.designationId || undefined,
      email: user?.email || "",
      initials: user?.initials || "",
      name: user?.name || "",
      phoneNumber: user?.phoneNumber || "",
      roleId: user?.roleId || undefined,
      state: user?.state || "",
      timezoneId: user?.timezoneId || undefined,
    },
  });

  const closeModal = useCallback(() => setOpenModal(null), []);
  const handleSubmit = hookFormSubmit((data) => {
    const payload = canEditUserRole ? data : { ...data, roleId: undefined };

    updateProfile(payload);
  });

  const tabs = useUsersTabs(user?.roleId);

  if (!loggedInUser || !user) return null;

  const canDeleteAndArchive =
    loggedInUser.id !== user.id &&
    canDeleteOrArchiveUser(loggedInUser.roleId, user.roleId);

  const showImpersonateButton =
    hasRole([
      USER_ROLES.SUPER_ADMIN,
      USER_ROLES.TEAM_LEADER,
      USER_ROLES.CUSTOMER_SUCCESS,
    ]) && user?.roleId !== UserRoleEnum.SuperAdmin;

  const canEdit = hasRole([
    USER_ROLES.OWNER,
    USER_ROLES.ADMIN,
    USER_ROLES.SUPER_ADMIN,
    USER_ROLES.CUSTOMER_SUCCESS,
  ]);

  const canEditEmail = hasRole([USER_ROLES.SUPER_ADMIN]);
  const showExtendedFields = isCustomerRole(user.roleId);

  const availableRoles = allAvailableRoles?.filter((role) =>
    isCustomerRole(user.roleId) ? isCustomerRole(role.id) : isTeamRole(role.id),
  );

  const canEditUserRole =
    canEdit && !!availableRoles?.some((role) => role.id === user.roleId);

  const displayedRoles = canEditUserRole
    ? availableRoles
    : [{ displayName: user.roleDisplayName, id: user.roleId }];

  const roleField = (
    <Controller
      control={control}
      name="roleId"
      render={({ field }) => (
        <TextField>
          <Label>User role</Label>
          <Select
            isDisabled={!canEditUserRole}
            onSelectionChange={field.onChange}
            selectedKey={field.value}
          >
            <SelectButton />
            <SelectPopover>
              <SelectListBox className="max-h-64">
                {displayedRoles?.map((role) => (
                  <SelectListItem id={role.id} key={role.id}>
                    {role.displayName}
                  </SelectListItem>
                ))}
              </SelectListBox>
            </SelectPopover>
          </Select>
        </TextField>
      )}
    />
  );

  const nameField = (
    <Controller
      control={control}
      name="name"
      render={({ field }) => (
        <TextField>
          <Label>Name</Label>
          <Input
            {...field}
            disabled={!canEdit}
            placeholder="Name"
            value={field.value || ""}
          />
        </TextField>
      )}
    />
  );

  const manageAccountContent = (
    <>
      <div className="px-xl lg:px-3xl py-xl lg:py-2xl border-b border-border-secondary">
        <div className="flex flex-col lg:flex-row items-center lg:items-start gap-6">
          <div className="flex flex-col items-center gap-2">
            <Avatar
              alt={user.name || "User"}
              className="size-20 rounded-full"
              initials={user.initials}
              src={user.avatar || ""}
            />
            <Button
              isDisabled={isAvatarUploading}
              onPress={handleChangePhoto}
              size="sm"
              variant="secondary"
            >
              Change photo
            </Button>
            <Button
              color="destructive"
              isDisabled={isAvatarUploading || !user.avatar}
              onPress={handleRemove}
              size="sm"
              variant="tertiary"
            >
              Remove
            </Button>
            <input
              accept="image/*"
              className="hidden"
              onChange={handleFileSelect}
              ref={fileInputRef}
              type="file"
            />
            {cropImageSrc && (
              <AvatarCropModal
                imageSrc={cropImageSrc}
                isOpen
                onApply={handleCropApply}
                onClose={handleCropClose}
              />
            )}
          </div>

          <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-4">
            {isTeamRole(user.roleId) ? (
              <>
                {roleField}

                <ProfileTeamFields
                  control={control}
                  isDisabled={!canEdit}
                  isSuperAdmin={user.roleId === UserRoleEnum.SuperAdmin}
                  roleId={user.roleId}
                  showDesignationOnly
                />

                {nameField}

                <Controller
                  control={control}
                  name="email"
                  render={({ field }) => (
                    <TextField
                      className={
                        user.roleId === UserRoleEnum.SuperAdmin
                          ? "col-span-full"
                          : ""
                      }
                    >
                      <Label>Email</Label>
                      <Input
                        {...field}
                        disabled={!canEditEmail}
                        onBlur={(e) => {
                          field.onBlur();
                          if (canEditEmail && e.target.value !== user.email) {
                            updateEmail({ email: e.target.value });
                          }
                        }}
                        placeholder="mail@nlc.com"
                        type="email"
                        value={field.value || ""}
                      />
                    </TextField>
                  )}
                />

                <ProfileTeamFields
                  control={control}
                  isDisabled={!canEdit}
                  isSuperAdmin={user.roleId === UserRoleEnum.SuperAdmin}
                  roleId={user.roleId}
                  showInitialsAndTeamOnly
                  team={user.teamName || ""}
                />

                {/* TODO: This feature will be refactored and improved in the future. 
                {![UserRoleEnum.Designer, UserRoleEnum.SuperAdmin].includes(
                  user.roleId,
                ) && (
                  <TagListField
                    className="col-span-full"
                    emptyMessage="No accounts assigned"
                    getItemHref={(id) => `/projects/${id}`}
                    isDisabled={!canEdit}
                    items={userProjectItems}
                    label="Accounts"
                    onItemsChange={(keys) => updateProjects(keys.map(Number))}
                    options={
                      user.roleId === UserRoleEnum.CustomerSuccess
                        ? (allProjects ?? [])
                        : (teamProjects ?? [])
                    }
                  />
                )} */}
              </>
            ) : (
              <>
                {nameField}

                {roleField}

                <Controller
                  control={control}
                  name="email"
                  render={({ field }) => (
                    <TextField
                      className={showExtendedFields ? "" : "col-span-full"}
                    >
                      <Label>Email</Label>
                      <Input
                        {...field}
                        disabled={!canEditEmail}
                        onBlur={(e) => {
                          field.onBlur();
                          if (canEditEmail && e.target.value !== user.email) {
                            updateEmail({ email: e.target.value });
                          }
                        }}
                        placeholder="mail@nlc.com"
                        type="email"
                        value={field.value || ""}
                      />
                    </TextField>
                  )}
                />
              </>
            )}

            {showExtendedFields && (
              <>
                <Controller
                  control={control}
                  name="timezoneId"
                  render={({ field }) => (
                    <TextField>
                      <Label>Timezone</Label>
                      <ComboBox
                        allowsEmptyCollection
                        isDisabled={!canEditUserRole}
                        menuTrigger="input"
                        onSelectionChange={field.onChange}
                        selectedKey={field.value}
                      >
                        <ComboBoxGroup>
                          <ComboBoxInput placeholder="Select a timezone" />
                          <ComboBoxButton />
                        </ComboBoxGroup>
                        <ComboBoxPopover>
                          <ComboBoxListBox className="max-h-64">
                            {timezones?.map((tz) => (
                              <ComboBoxListItem id={tz.id} key={tz.id}>
                                {tz.displayName}
                              </ComboBoxListItem>
                            ))}
                          </ComboBoxListBox>
                        </ComboBoxPopover>
                      </ComboBox>
                    </TextField>
                  )}
                />

                <Controller
                  control={control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <TextField className="col-span-full">
                      <Label>Phone number</Label>
                      <Input
                        {...field}
                        placeholder="000 00 00000000"
                        value={field.value || ""}
                      />
                    </TextField>
                  )}
                />

                <TagListField
                  className="col-span-full"
                  emptyMessage="No accounts assigned"
                  getItemHref={
                    isTeamRole(loggedInUser.roleId)
                      ? (id) => `/projects/${id}`
                      : undefined
                  }
                  isDisabled={
                    hasRole([USER_ROLES.OWNER]) ||
                    !canEditUserRole ||
                    (ownerProjects?.length ?? 0) < 2 ||
                    user.roleId === UserRoleEnum.Owner
                  }
                  items={userProjectItems}
                  label="Accounts"
                  onItemsChange={(keys) => updateProjects(keys.map(Number))}
                  options={ownerProjects ?? []}
                />

                {user.roleId === UserRoleEnum.Collaborator && (
                  <ProfileCollaboratorFields
                    isDisabled={!canEditUserRole}
                    onBrandChange={(keys) => updateBrands(keys.map(Number))}
                    onRequestChange={(keys) => updateRequests(keys.map(Number))}
                    userId={userId}
                    viewedUserProjectIds={viewedUserProjectIds}
                  />
                )}

                {isCustomerRole(user.roleId) && (
                  <ProfileOwnerAdminFields
                    control={control}
                    isDisabledCustomerField={
                      !(
                        hasRole([
                          USER_ROLES.SUPER_ADMIN,
                          USER_ROLES.CUSTOMER_SUCCESS,
                        ]) && user.roleId === UserRoleEnum.Owner
                      )
                    }
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="px-6 lg:px-10 py-5 flex justify-end border-t border-border-secondary">
        <Button type="submit">Save</Button>
      </div>
    </>
  );

  return (
    <div className="p-xl">
      <FeaturePageLayout
        backButton
        headerContent={
          <div className="flex flex-col lg:flex-row justify-end items-center gap-xl w-full">
            {user.isArchived && (
              <Badge isRounded size="medium" variant="secondary">
                <TagIcon size={14} />
                Archived
              </Badge>
            )}
            <div className="flex flex-row flex-wrap items-center gap-xs lg:gap-md">
              {showImpersonateButton ? (
                <div className="order-first lg:order-4">
                  <ImpersonateUser id={String(userData?.user.id ?? "")} />
                </div>
              ) : null}
              {canDeleteAndArchive ? (
                <>
                  <div className="order-2 lg:order-3">
                    <Button
                      isDisabled={user.isArchived}
                      onPress={() => setOpenModal("password")}
                      size="sm"
                      variant="secondary"
                    >
                      <LockIcon />
                      Update password
                    </Button>
                  </div>
                  <div className="order-3 lg:order-2">
                    <Button
                      color="gray"
                      onPress={() =>
                        setOpenModal(user.isArchived ? "unarchive" : "archive")
                      }
                      size="sm"
                      variant="secondary"
                    >
                      <ArchiveIcon />
                      {user.isArchived ? "Unarchive" : "Archive"}
                    </Button>
                  </div>
                  <div className="order-last lg:order-1">
                    <Button
                      color="destructive"
                      isDisabled={user.isArchived}
                      onPress={() => setOpenModal("delete")}
                      size="sm"
                      variant="tertiary"
                    >
                      <Trash2Icon />
                      Delete
                    </Button>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        }
        isLoading={isLoading}
        title="Edit Profile"
      >
        {isLoading ? (
          <div className="bg-bg-primary p-5">
            <div className="flex items-center justify-center py-12">
              <p className="text-sm text-text-tertiary">
                <Loader className="bg-bg-primary" />
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            <form onSubmit={handleSubmit}>
              <div className="bg-bg-primary rounded-b-lg">
                {tabs.length > 1 ? (
                  <Tabs defaultSelectedKey="manage">
                    <div className="py-lg">
                      <TabList className="px-xl lg:px-2xl flex overflow-x-auto">
                        {tabs.map((tab) => (
                          <Tab id={tab.id} key={tab.id}>
                            {tab.label}
                          </Tab>
                        ))}
                      </TabList>
                    </div>
                    {tabs.map((tab) => (
                      <TabPanel id={tab.id} key={tab.id}>
                        {tab.id === "manage" && manageAccountContent}
                        {tab.id === "performance" && (
                          <div className="px-xl py-5 lg:px-10">
                            <PerformanceTab
                              projects={userData?.projects?.map((p) => ({
                                id: p.id,
                                name: p.name,
                              }))}
                              userId={userId}
                            />
                          </div>
                        )}
                        {tab.id === "requests" && (
                          <AssignedToMeTab user={user} />
                        )}
                        {tab.id === "team" && (
                          <TeamRequestsTab userId={userId} />
                        )}
                      </TabPanel>
                    ))}
                  </Tabs>
                ) : (
                  manageAccountContent
                )}
              </div>
            </form>
            <UpdateUserPassword
              isOpen={openModal === "password"}
              onClose={closeModal}
            />
            <DeleteUser
              onClose={closeModal}
              openModal={openModal === "delete"}
              userdata={userData}
            />
            <ArchiveUser
              onClose={closeModal}
              openModal={openModal === "archive"}
              userdata={userData}
            />
            <UnarchiveUser
              onClose={closeModal}
              openModal={openModal === "unarchive"}
              userdata={userData}
            />
          </div>
        )}
      </FeaturePageLayout>
    </div>
  );
}
